import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { errorHandler } from "_services/errorHandler";
import {
  useTransportResourceServiceV1CreateVendorMutation,
  useTransportResourceServiceV1CreateVendorDraftMutation,
  TransportResourceCreateVendorReq,
  TransportResourceCreateVendorDraftReq,
  useTransportResourceServiceV1UploadVendorLogoMutation,
  TransportResourceServiceV1UploadVendorLogoBody,
  vendorApi,
} from "_services/modules/transport-resource/vendor";
import {
  VendorAddressFormValues,
  VendorAddressItem,
  VendorBranchFormValues,
  VendorBranchItem,
  VendorInformationFormValues,
} from "_interfaces/vendor.interfaces";
import { isHeadquarterCategory, getAvailableBranchOptions } from "data/address-category";
import { useAppTranslation } from "locale/useAppTranslation";
import { vendorPageRouteName } from "modules/master-data/vendor/constants/routes";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";

export type { VendorAddressItem, VendorBranchItem } from "_interfaces/vendor.interfaces";

const vendorInfoSchema = yup
  .object({
    vendorType: yup
      .mixed<"individual" | "company">()
      .oneOf(["individual", "company"])
      .required(),
    vendorName: yup.string().required("Vendor name is required"),
    vendorId: yup.string().notRequired(),
    npwp: yup.string().required("NPWP is required"),
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Vendor email is required"),
    contactPersonName: yup.string().required("Contact person name is required"),
    contactPersonPhoneCountryCode: yup.string().required(),
    contactPersonPhoneNumber: yup
      .string()
      .required("Contact person phone number is required"),
    legalName: yup.string().when("vendorType", {
      is: "company",
      then: (schema) => schema.required("Legal name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    status: yup.mixed<"active" | "inactive">().oneOf(["active", "inactive"]).required(),
  })
  .required();

const vendorBranchSchema = yup
  .object({
    branchName: yup.string().required("Branch name is required"),
    picName: yup.string().required("PIC name is required"),
    picEmail: yup
      .string()
      .email("Please enter a valid email")
      .required("PIC email is required"),
    picCountryCode: yup.string().required(),
    picPhoneNumber: yup.string().required("PIC phone number is required"),
  })
  .required();

const vendorAddressSchema = yup
  .object({
    addressName: yup.string().required("Address name is required"),
    addressCategory: yup.string().required("Address category is required"),
    branch: yup.string().required("Branch is required"),
    country: yup.string().required("Country is required"),
    province: yup.string().required("Province is required"),
    city: yup.string().required("City is required"),
    postalCode: yup.string().required("Postal code is required"),
    fullAddress: yup.string().required("Full address is required"),
    mapsUrl: yup.string().required("Google Maps URL is required"),
    isBillingAddress: yup.boolean().default(false),
  })
  .required();

const initialBranchItems: VendorBranchItem[] = [];

const useCreateVendorForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vendor");
  const [createVendor, { isLoading: isCreatingVendor }] =
    useTransportResourceServiceV1CreateVendorMutation();
  const [createVendorDraft, { isLoading: isCreatingVendorDraft }] =
    useTransportResourceServiceV1CreateVendorDraftMutation();
  const [uploadLogo] = useTransportResourceServiceV1UploadVendorLogoMutation();

  const vendorInfoForm = useForm<VendorInformationFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorInfoSchema),
    defaultValues: {
      vendorType: "company",
      vendorName: "",
      vendorId: "",
      npwp: "",
      email: "",
      contactPersonName: "",
      contactPersonPhoneCountryCode: "+62",
      contactPersonPhoneNumber: "",
      legalName: "",
      status: "active",
    },
  });

  const vendorBranchForm = useForm<VendorBranchFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorBranchSchema),
    defaultValues: {
      branchName: "",
      picName: "",
      picEmail: "",
      picCountryCode: "+62",
      picPhoneNumber: "",
    },
  });

  const vendorAddressForm = useForm<VendorAddressFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorAddressSchema),
    defaultValues: {
      addressName: "",
      addressCategory: "branch",
      branch: "",
      country: "",
      province: "",
      city: "",
      postalCode: "",
      fullAddress: "",
      mapsUrl: "",
      isBillingAddress: false,
    },
  });

  const [openSections, setOpenSections] = useState({
    vendorInformation: true,
    vendorBranch: false,
    vendorAddress: false,
  });
  const [branchItems, setBranchItems] = useState<VendorBranchItem[]>(initialBranchItems);
  const [addressItems, setAddressItems] = useState<VendorAddressItem[]>([]);
  const [editingBranchIndex, setEditingBranchIndex] = useState<number | null>(null);
  const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-create"),
        hasBackButton: true,
      }),
    );
  }, [dispatch, t]);

  const branchOptions = useMemo(
    () => getAvailableBranchOptions(branchItems, addressItems, editingAddressIndex),
    [branchItems, addressItems, editingAddressIndex],
  );

  const hasBillingAddress = useMemo(
    () => addressItems.some((item) => item.isBillingAddress),
    [addressItems],
  );

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const openSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: true,
    }));
  };

  const handleNextFromInformation = async () => {
    setOpenSections((current) => ({
      ...current,
      vendorInformation: false,
      vendorBranch: true,
    }));
  };

  const handleNextFromBranch = async () => {
    setOpenSections((current) => ({
      ...current,
      vendorBranch: false,
      vendorAddress: true,
    }));
  };

  const handleAddBranch = async () => {
    const isValid = await vendorBranchForm.trigger();
    if (!isValid) {
      return;
    }

    const values = vendorBranchForm.getValues();
    const nextBranch = {
      branchName: values.branchName,
      picName: values.picName,
      picEmail: values.picEmail,
      picCountryCode: values.picCountryCode,
      picPhoneNumber: values.picPhoneNumber,
    };

    if (editingBranchIndex !== null) {
      const oldBranchName = branchItems[editingBranchIndex].branchName;
      const newBranchName = values.branchName;
      setBranchItems((current) =>
        current.map((item, index) =>
          index === editingBranchIndex ? nextBranch : item,
        ),
      );
      if (oldBranchName !== newBranchName) {
        setAddressItems((current) =>
          current.map((item) =>
            item.branch === oldBranchName ? { ...item, branch: newBranchName } : item,
          ),
        );
      }
      setEditingBranchIndex(null);
    } else {
      setBranchItems((current) => [...current, nextBranch]);
    }

    vendorBranchForm.reset({
      branchName: "",
      picName: "",
      picEmail: "",
      picCountryCode: "+62",
      picPhoneNumber: "",
    });
  };

  const handleEditBranch = (index: number) => {
    const item = branchItems[index];
    setEditingBranchIndex(index);
    vendorBranchForm.reset({
      branchName: item.branchName,
      picName: item.picName,
      picEmail: item.picEmail,
      picCountryCode: item.picCountryCode,
      picPhoneNumber: item.picPhoneNumber,
    });
  };

  const handleDeleteBranch = (index: number) => {
    if (branchItems.length <= 1) {
      toast.error(t("toast-branch-required") || "Vendor must have at least one branch");
      return;
    }
    const deletedBranchName = branchItems[index].branchName;
    setBranchItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setAddressItems((current) => current.filter((item) => item.branch !== deletedBranchName));
    if (editingBranchIndex === index) {
      setEditingBranchIndex(null);
    }
  };

  const handleAddAddress = async () => {
    const isValid = await vendorAddressForm.trigger();
    if (!isValid) {
      return;
    }

    const values = vendorAddressForm.getValues();
    const nextAddress: VendorAddressItem = {
      addressName: values.addressName,
      addressCategory: values.addressCategory,
      branch: values.branch,
      fullAddress: values.fullAddress,
      country: values.country,
      province: values.province,
      city: values.city,
      postalCode: values.postalCode,
      mapsUrl: values.mapsUrl,
      isBillingAddress: values.isBillingAddress,
      countryName: values.countryName || (values.country === "102" ? "Indonesia" : values.country),
      provinceName: values.provinceName || values.province,
      cityName: values.cityName || values.city,
    };

    const targetBranchIndex = branchItems.findIndex((branch) => branch.branchName === values.branch);
    if (targetBranchIndex === -1) {
      toast.error(t("toast-invalid-branch-link"));
      return;
    }

    const isBranchAlreadyUsed = addressItems.some(
      (item, index) => index !== editingAddressIndex && item.branch === values.branch,
    );
    if (isBranchAlreadyUsed) {
      toast.error("Branch already has an address assigned");
      return;
    }

    if (isHeadquarterCategory(values.addressCategory)) {
      const isHqAlreadyUsed = addressItems.some(
        (item, index) => index !== editingAddressIndex && isHeadquarterCategory(item.addressCategory),
      );
      if (isHqAlreadyUsed) {
        toast.error("Only one Headquarter address is allowed");
        return;
      }
    }

    if (editingAddressIndex !== null) {
      setAddressItems((current) =>
        current.map((item, index) =>
          index === editingAddressIndex ? nextAddress : item,
        ),
      );
      setEditingAddressIndex(null);
    } else {
      setAddressItems((current) => [...current, nextAddress]);
    }

    vendorAddressForm.reset({
      addressName: "",
      addressCategory: "branch",
      branch: "",
      country: "",
      province: "",
      city: "",
      postalCode: "",
      fullAddress: "",
      mapsUrl: "",
      isBillingAddress: false,
      countryName: "",
      provinceName: "",
      cityName: "",
    });
  };

  const handleEditAddress = (index: number) => {
    const item = addressItems[index];
    setEditingAddressIndex(index);
    vendorAddressForm.reset({
      addressName: item.addressName,
      addressCategory: item.addressCategory,
      branch: item.branch,
      country: item.country,
      province: item.province,
      city: item.city,
      postalCode: item.postalCode,
      fullAddress: item.fullAddress,
      mapsUrl: item.mapsUrl,
      isBillingAddress: item.isBillingAddress,
      countryName: item.countryName,
      provinceName: item.provinceName,
      cityName: item.cityName,
    });
  };

  const handleDeleteAddress = (index: number) => {
    const targetAddress = addressItems[index];
    if (targetAddress) {
      const branchAddressesCount = addressItems.filter(
        (item) => item.branch === targetAddress.branch,
      ).length;
      if (branchAddressesCount <= 1) {
        toast.error(
          t("toast-all-branches-must-have-address") ||
            "Each branch must have at least one address",
        );
        return;
      }
    }
    setAddressItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    if (editingAddressIndex === index) {
      setEditingAddressIndex(null);
    }
  };

  const handleConfirmAddress = () => {
    if (addressItems.length === 0) {
      toast.error(t("toast-address-required"));
      return;
    }
    if (!hasBillingAddress) {
      toast.error(t("toast-billing-required"));
      return;
    }
    toast.success(t("toast-address-confirmed"));
  };

  const cleanPhone = (countryCode: string, phoneNumber: string) => {
    const code = countryCode.replace(/\s+/g, "");
    let num = phoneNumber.replace(/\D/g, "");
    if (num.startsWith("0")) {
      num = num.slice(1);
    }
    return `${code}${num}`;
  };

  const handleSaveDraft = async () => {
    const vendorInfo = vendorInfoForm.getValues();
    if (!vendorInfo.vendorName || !vendorInfo.vendorName.trim()) {
      openSection("vendorInformation");
      vendorInfoForm.setError("vendorName", {
        type: "manual",
        message: "Vendor name is required",
      });
      toast.error("Vendor Name is required to save draft");
      return;
    }

    const branchesPayload = branchItems.map((branchItem) => {
      const relatedAddresses = addressItems.filter(
        (address) => address.branch === branchItem.branchName,
      );

      return {
        branch_name: branchItem.branchName,
        pic_name: branchItem.picName,
        pic_email: branchItem.picEmail,
        pic_phone: cleanPhone(branchItem.picCountryCode, branchItem.picPhoneNumber),

        addresses: relatedAddresses.map((addressItem) => ({
          address_name: addressItem.addressName,
          address_category: addressItem.addressCategory,
          full_address: addressItem.fullAddress,
          province_id: addressItem.province,
          city_id: addressItem.city,
          postal_code: addressItem.postalCode,
          google_maps_url: addressItem.mapsUrl,
          is_billing_address: addressItem.isBillingAddress,
        })),
      };
    });

    const draftPayload: TransportResourceCreateVendorDraftReq = {
      ...(vendorInfo.vendorName ? { vendor_name: vendorInfo.vendorName } : {}),
      ...(vendorInfo.legalName ? { legal_name: vendorInfo.legalName } : {}),
      ...(vendorInfo.npwp ? { npwp: vendorInfo.npwp } : {}),
      ...(vendorInfo.vendorType ? { vendor_type: vendorInfo.vendorType } : {}),
      ...(vendorInfo.vendorId?.trim() ? { vendor_code: vendorInfo.vendorId.trim() } : {}),
      ...(vendorInfo.email ? { email: vendorInfo.email } : {}),
      ...(vendorInfo.contactPersonName ? { contact_person_name: vendorInfo.contactPersonName } : {}),
      ...(vendorInfo.contactPersonPhoneNumber
        ? {
            contact_person_phone_number: cleanPhone(
              vendorInfo.contactPersonPhoneCountryCode,
              vendorInfo.contactPersonPhoneNumber,
            ),
          }
        : {}),
      ...(branchesPayload.length > 0 ? { branches: branchesPayload } : {}),
    };

    try {
      const response = await createVendorDraft({
        transportResourceCreateVendorDraftReq: draftPayload,
      }).unwrap();

      const vendorLogoFile = vendorInfoForm.getValues("vendorLogoFile");
      if (vendorLogoFile && response.vendor_id) {
        const formData = new FormData();
        formData.append("file", vendorLogoFile);
        await uploadLogo({
          id: response.vendor_id,
          transportResourceServiceV1UploadVendorLogoBody: formData as TransportResourceServiceV1UploadVendorLogoBody,
        }).unwrap();
      }

      dispatch(vendorApi.util.invalidateTags([{ type: "Vendor", id: "DRAFT_LIST" }, { type: "Vendor", id: "LIST" }]));
      toast.success(t("toast-draft-saved") || "Draft saved");
      navigate(vendorPageRouteName);
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleSave = async () => {
    const isValidVendorInfo = await vendorInfoForm.trigger();
    if (!isValidVendorInfo) {
      openSection("vendorInformation");
      toast.error(t("toast-complete-required-fields"));
      return;
    }

    if (branchItems.length === 0) {
      openSection("vendorBranch");
      toast.error(t("toast-branch-required"));
      return;
    }

    if (addressItems.length === 0) {
      openSection("vendorAddress");
      toast.error(t("toast-address-required"));
      return;
    }

    const hasAddressForEveryBranch = branchItems.every((branch) =>
      addressItems.some((address) => address.branch === branch.branchName),
    );

    if (!hasAddressForEveryBranch) {
      openSection("vendorAddress");
      toast.error(t("toast-all-branches-must-have-address"));
      return;
    }

    if (!hasBillingAddress) {
      openSection("vendorAddress");
      toast.error(t("toast-billing-required"));
      return;
    }

    const vendorInfo = vendorInfoForm.getValues();

    const branchesPayload = branchItems.map((branchItem) => {
      const relatedAddresses = addressItems.filter(
        (address) => address.branch === branchItem.branchName,
      );

      return {
        branch_name: branchItem.branchName,
        pic_name: branchItem.picName,
        pic_email: branchItem.picEmail,
        pic_phone: cleanPhone(branchItem.picCountryCode, branchItem.picPhoneNumber),

        addresses: relatedAddresses.map((addressItem) => ({
          address_name: addressItem.addressName,
          address_category: addressItem.addressCategory,
          full_address: addressItem.fullAddress,
          province_id: addressItem.province,
          city_id: addressItem.city,
          postal_code: addressItem.postalCode,
          google_maps_url: addressItem.mapsUrl,
          is_billing_address: addressItem.isBillingAddress,
        })),
      };
    });

    try {
      const createReq: TransportResourceCreateVendorReq = {
        vendor_name: vendorInfo.vendorName,
        legal_name:
          vendorInfo.vendorType === "company" && vendorInfo.legalName
            ? vendorInfo.legalName
            : undefined,
        npwp: vendorInfo.npwp,
        vendor_type: vendorInfo.vendorType,
        status: vendorInfo.status,

        email: vendorInfo.email,

        contact_person_name: vendorInfo.contactPersonName,

        contact_person_phone_number: cleanPhone(
          vendorInfo.contactPersonPhoneCountryCode,
          vendorInfo.contactPersonPhoneNumber,
        ),

        branches: branchesPayload,
        ...(vendorInfo.vendorId?.trim() ? { vendor_code: vendorInfo.vendorId.trim() } : {}),
      };

      const response = await createVendor({
        transportResourceCreateVendorReq: createReq,
      }).unwrap();

      const vendorLogoFile = vendorInfoForm.getValues("vendorLogoFile");

      if (vendorLogoFile && response.vendor_id) {
        const formData = new FormData();
        formData.append("file", vendorLogoFile);

        await uploadLogo({
          id: response.vendor_id,
          transportResourceServiceV1UploadVendorLogoBody: formData as TransportResourceServiceV1UploadVendorLogoBody,
        }).unwrap();
      }

      dispatch(vendorApi.util.invalidateTags([{ type: "Vendor", id: "LIST" }, { type: "Vendor", id: "DRAFT_LIST" }]));
      toast.success(
        t("toast-create-success", {
          name: vendorInfo.vendorName,
        }),
      );

      navigate(vendorPageRouteName);
    } catch (error) {
      errorHandler(error);
    }
  };

  const isDirty =
    vendorInfoForm.formState.isDirty ||
    vendorBranchForm.formState.isDirty ||
    vendorAddressForm.formState.isDirty ||
    branchItems.length > 0 ||
    addressItems.length > 0;

  return {
  t,
  openSections,
  toggleSection,
  vendorInfoForm,
  vendorBranchForm,
  vendorAddressForm,
  branchItems,
  addressItems,
  branchOptions,
  hasBillingAddress,
  editingBranchIndex,
  editingAddressIndex,
  isCreatingVendor:
    isCreatingVendor || isCreatingVendorDraft,
  isDirty,
  handleNextFromInformation,
  handleNextFromBranch,
  handleAddBranch,
  handleEditBranch,
  handleDeleteBranch,
  handleAddAddress,
  handleEditAddress,
  handleDeleteAddress,
  handleConfirmAddress,
  handleSaveDraft,
  handleSave,
};
};

export default useCreateVendorForm;
